import { Button, TextInput } from '@components/v2/ui';
import ColorInput from '@components/v2/ui/ColorInput/ColorInput';
import { useCreateTag } from '@features/offsets/api/create-tag';
import { TOffsetTag } from '@global-types/index';
import {
	Drawer,
	DrawerProps,
	Flex,
	Group,
	Stack,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import useStore from '@store/index';
import { useTranslation } from 'react-i18next';
import classes from './DrawerCreateTag.module.css';

const COLORS = [
	'#2F3A5F',
	'#9BA6B7',
	'#F0F3F8',
	'#617EEF',
	'#D4DDE7',
	'#E8F4FF',
	'#FF381C',
	'#FDBFCE',
	'#FEE5EB',
	'#2C8C5E',
	'#BAF3DA',
	'#DDF9ED',
	'#F5DF4D',
	'#F7C80A',
	'#FEF2DC',
];

const FlagFR = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 18 18"
		xmlns="http://www.w3.org/2000/svg"
	>
		<defs>
			<clipPath id="circle">
				<circle cx="9" cy="9" r="9" />
			</clipPath>
		</defs>
		<g clipPath="url(#circle)">
			<rect width="6" height="18" fill="#002395" />
			<rect x="6" width="6" height="18" fill="#FFFFFF" />
			<rect x="12" width="6" height="18" fill="#ED2939" />
		</g>
	</svg>
);

const FlagEN = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 18 18"
		xmlns="http://www.w3.org/2000/svg"
	>
		<defs>
			<clipPath id="circleEn">
				<circle cx="9" cy="9" r="9" />
			</clipPath>
		</defs>
		<g clipPath="url(#circleEn)">
			<rect width="18" height="18" fill="#012169" />
			<path
				d="M0 0l18 18M18 0L0 18"
				stroke="#fff"
				strokeWidth="2.5"
			/>
			<path
				d="M0 0l18 18M18 0L0 18"
				stroke="#C8102E"
				strokeWidth="1.5"
			/>
			<path
				d="M9 0v18M0 9h18"
				stroke="#fff"
				strokeWidth="4"
			/>
			<path
				d="M9 0v18M0 9h18"
				stroke="#C8102E"
				strokeWidth="2.5"
			/>
		</g>
	</svg>
);

const DrawerCreateTag = (
	props: DrawerProps & {
		onSuccess: (idTag: TOffsetTag) => void;
	}
) => {
	const { t } = useTranslation();
	const { setOffsetCreationDatas, offsetCreationDatas } =
		useStore();

	const { mutate, isPending } = useCreateTag({
		mutationConfig: {
			onSuccess(data) {
				props.onSuccess(data);
				setOffsetCreationDatas({
					...offsetCreationDatas,
					tags: [
						...(offsetCreationDatas.tags || []),
						data.id,
					],
				});
				props.onClose();
				form.reset();
			},
		},
	});

	const form = useForm({
		mode: 'uncontrolled',
		initialValues: {
			fr: '',
			en: '',
			color: '',
		},
		validate: {
			fr: (value) => {
				const frRegex =
					/^[-_0-9a-zàâçéèêëîïôûùüÿñæœ ]+$/i;
				const trimmed = value.trim();

				if (!trimmed) return t('tagValidation.required');

				if (trimmed.length < 2 || trimmed.length > 30) {
					return t('tagValidation.tooLong');
				}

				return frRegex.test(trimmed)
					? null
					: t('tagValidation.invalidChars');
			},
			en: (value) => {
				const enRegex = /^[-_0-9a-z ]+$/i;
				const trimmed = value.trim();

				if (!trimmed) return t('tagValidation.required');

				if (trimmed.length < 2 || trimmed.length > 30) {
					return t('tagValidation.tooLong');
				}

				return enRegex.test(trimmed)
					? null
					: t('tagValidation.invalidChars');
			},
			color: (value) =>
				!value ? t('tagValidation.required') : null,
		},
	});

	const handleSubmit = (values: typeof form.values) => {
		const requestBody = {
			value: {
				fr: values.fr,
				en: values.en,
			},
			color: values.color,
		};

		mutate({ body: requestBody });
	};

	return (
		<Drawer
			opened={props.opened}
			onClose={props.onClose}
			title={t('tagManager.addNew')}
			position="right"
			size="656px"
			zIndex={99}
			classNames={classes}
		>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack h={'82vh'} justify="space-between">
					<Stack>
						<TextInput
							label={
								<Group align="center" gap={5}>
									<FlagFR /> {' Nouveau tag en Francais'}{' '}
									<span
										style={{
											color: 'red',
										}}
									>
										*
									</span>
								</Group>
							}
							key={form.key('fr')}
							placeholder="Nouveau tag"
							{...form.getInputProps('fr')}
						/>
						<TextInput
							label={
								<Group gap={5}>
									<FlagEN /> {' New tag in english'}{' '}
									<span
										style={{
											color: 'red',
										}}
									>
										*
									</span>
								</Group>
							}
							key={form.key('en')}
							placeholder="New tag"
							{...form.getInputProps('en')}
						/>
						<ColorInput
							withAsterisk
							label={t('global.tags')}
							format="hex"
							swatches={COLORS}
							key={form.key('color')}
							placeholder="#FFFFFF"
							{...form.getInputProps('color')}
						/>
					</Stack>
					<Flex justify="space-between" gap={'12px'}>
						<Button
							fullWidth
							variant="outline"
							onClick={() => {
								props.onClose();
								form.reset();
							}}
						>
							{t('global.cancel')}
						</Button>
						<Button
							type="submit"
							fullWidth
							variant="primary"
							loading={isPending}
						>
							{t('tagManager.addNew')}
						</Button>
					</Flex>
				</Stack>
			</form>
		</Drawer>
	);
};

export default DrawerCreateTag;
